/**
 * The docked Audio Overview bar's height, shared with layout that has to keep
 * clear of it (ORA-735). CSS reads the variable; JS geometry (the companion
 * window) reads `readAudioBarOffset` and listens for the change event, since a
 * docking bar changes the usable viewport without a `resize`.
 */
/** Room the docked bar takes above the layout's own bottom edge or nav row. */
export const AUDIO_BAR_OFFSET_VAR = '--audio-bar-offset';
/** Distance from the viewport bottom to the bar's top edge (toasts use it). */
export const AUDIO_BAR_CLEARANCE_VAR = '--audio-bar-clearance';
export const AUDIO_BAR_OFFSET_EVENT = 'ora:audio-bar-offset-change';

/** The docked bar's height in px; 0 when nothing is docked. */
export const readAudioBarOffset = (): number => {
  if (typeof document === 'undefined') return 0;

  const value = Number.parseFloat(
    document.documentElement.style.getPropertyValue(AUDIO_BAR_OFFSET_VAR)
  );

  return Number.isFinite(value) && value > 0 ? value : 0;
};

const px = (value: number | null) => (value && value > 0 ? `${value}px` : '');

/**
 * Sets (or, with null, clears) the offset and clearance, and tells JS
 * geometry when the offset changed.
 */
export const publishAudioBarOffset = (
  heightPx: number | null,
  clearancePx: number | null = heightPx
): void => {
  const root = document.documentElement;
  const nextClearance = px(clearancePx);

  if (nextClearance)
    root.style.setProperty(AUDIO_BAR_CLEARANCE_VAR, nextClearance);
  else root.style.removeProperty(AUDIO_BAR_CLEARANCE_VAR);

  const next = px(heightPx);
  if (root.style.getPropertyValue(AUDIO_BAR_OFFSET_VAR) === next) return;

  if (next) root.style.setProperty(AUDIO_BAR_OFFSET_VAR, next);
  else root.style.removeProperty(AUDIO_BAR_OFFSET_VAR);
  window.dispatchEvent(new Event(AUDIO_BAR_OFFSET_EVENT));
};
